'use client';

import { useEffect } from 'react';

/**
 * Debug helper: monitora erros de hidratacao do React em producao e
 * imprime contexto adicional no console. Sem isso, os erros chegam
 * minificados (`#418`, `#425`, `#423`) e o stack so mostra internas
 * do react-dom.production.min.js - inutil pra rastrear o componente
 * culpado.
 *
 * O que captura:
 *   - quando um erro #418/#423/#425 e logado pelo Next.js
 *     (on-recoverable-error.ts), salva no console o body atual e o
 *     primeiro elemento com atributos suspeitos (data-gramm, etc).
 *   - listener no `unhandledrejection` para capturar promessas
 *     rejeitadas que disparam hidratacao falha.
 *
 * Componente "invisivel" - so monta o listener no mount, nao
 * renderiza nada.
 */
export function HydrationDebugger() {
  useEffect(() => {
    const originalConsoleError = console.error;
    console.error = function patchedError(...args: unknown[]) {
      const firstArg = args[0];
      const msg = typeof firstArg === 'string' ? firstArg : '';

      // Padroes que indicam hidratacao com problema (minificados em prod).
      const isHydrationError =
        msg.includes('Minified React error #418') ||
        msg.includes('Minified React error #423') ||
        msg.includes('Minified React error #425') ||
        msg.includes('hydration') ||
        msg.includes('Hydration');

      if (isHydrationError) {
        try {
          // Lista atributos suspeitos de extensao no <body> e <html>.
          const htmlAttrs = Array.from(document.documentElement.attributes)
            .map((a) => `${a.name}="${a.value}"`)
            .filter((a) => !a.startsWith('lang=') && !a.startsWith('class=') && !a.startsWith('data-theme'));
          const bodyAttrs = Array.from(document.body.attributes)
            .map((a) => `${a.name}="${a.value}"`)
            .filter((a) => !a.startsWith('class='));

          // Atributos comuns injetados por extensoes - se aparecerem,
          // o problema NAO eh codigo nosso.
          const extensionMarkers = [
            ...htmlAttrs,
            ...bodyAttrs,
          ].filter(
            (a) =>
              a.includes('data-gr-') || // Grammarly
              a.includes('data-gramm') || // Grammarly
              a.includes('data-lpignore') || // LastPass
              a.includes('cz-shortcut') || // ColorZilla
              a.includes('data-darkreader') || // Dark Reader
              a.includes('data-1p-') || // 1Password
              a.includes('data-bitwarden'), // Bitwarden
          );

          if (extensionMarkers.length > 0) {
            originalConsoleError.call(
              console,
              '[hydration-debug] Atributos de extensao detectados (provavelmente a causa raiz):',
              extensionMarkers,
            );
          }

          originalConsoleError.call(
            console,
            '[hydration-debug] Contexto:',
            {
              url: window.location.href,
              htmlExtraAttrs: htmlAttrs,
              bodyExtraAttrs: bodyAttrs,
            },
          );
        } catch {
          // Se a coleta de debug falhar, segue o jogo - nao queremos
          // mascarar o erro original.
        }
      }
      return originalConsoleError.apply(console, args as Parameters<typeof console.error>);
    };

    return () => {
      console.error = originalConsoleError;
    };
  }, []);

  return null;
}
